// Firebase JS SDK init — modular only.
// -----------------------------------------------------------------
// Per docs/architecture/m3-spa-scaffold-plan.md § 2.5 (auth) + § 2.6
// (audit-fields rule) + dispatch m3-2-auth-rules-listener.md § 6.3.
//
// Reads VITE_FIREBASE_* values that Vite bakes into the bundle at
// build time. Mode-specific .env files (.env.staging vs
// .env.production) carry the per-environment Firebase config. Local
// `npm run dev` picks up .env.development.
//
// The values exported here (`auth`, `db`) are the modular-SDK
// singletons every consumer of Firebase in this app must import
// from. Do NOT re-init the app elsewhere; do NOT reach for the
// compat SDK (`firebase/compat/...`) — see dispatch § 3.6.

import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

// Read once at module-load. Vite replaces `import.meta.env.VITE_*`
// with literal strings during build; missing values become the
// literal `undefined` (string) which we guard against below.
const config: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate at module-load to fail fast on misconfiguration. A
// missing key here means the build's .env file is wrong; debugging
// gets easier when we throw at module-load instead of during the
// first sign-in attempt.
for (const [key, value] of Object.entries(config)) {
  if (!value) {
    throw new Error(
      `Firebase config missing: VITE_FIREBASE_${key
        .replace(/([A-Z])/g, '_$1')
        .toUpperCase()}`,
    );
  }
}

const app = initializeApp(config);

export const auth = getAuth(app);
export const db = getFirestore(app);

// Ops convenience (2026-10-04, #448): expose the auth instance on `window` so an
// operator can copy a fresh ID token from the devtools console for scripted admin
// calls (e.g. scripts/notification_admin_send.py --id-token). `auth` is NOT a
// secret — it is the same client-side object the page already uses; the ID token
// it yields is short-lived (~1h) and tied to the operator's own session.
//   Console:  await window.auth.currentUser.getIdToken()
(window as unknown as { auth?: typeof auth }).auth = auth;
// Storage SDK wiring per dispatch m4-2-plan-metadata-create-staging.md
// § 3.3 + § 6.1 + M4-F15. The `storage` singleton serves
// `<PlanMetadata>`'s cover-image upload helper
// (src/lib/storage/uploadCoverImage.ts) and any future M-phase
// Storage usage. M4.2 does NOT add `storage.rules` — Firebase
// defaults (admin-claim writes + anonymous reads) suffice for the
// `admin-uploads/tour-covers/...` path; verify lead-direct at
// M4.2-P1. M4.2 tests MOCK firebase/storage entirely (per § 3.7);
// no emulator-driven Storage coverage in this phase.
export const storage = getStorage(app);
// Functions SDK wiring per dispatch m4-5 § 6.13 + § 3.5 + § 0.1
// M4.5-pre-F4. The `functions` singleton serves the HTTPS Callable
// mutations (`httpsCallable(functions, 'format_tts_text')`,
// `'generate_intro_outro'` per <PipelinePanel> + <IntroOutroPanel>;
// the plan-level audio path in <TTSPanel> now uses
// `generate_audio_dispatch` per m-pipeline-6b1).
//
// REGION: 'europe-west1' per **M4.5-F2** — drift correction surfaced
// during sub-agent execution: dispatch § 6.13 skeleton specified
// 'us-central1' but the project's CFs all deploy to europe-west1
// (deploy.sh:81 REGION="europe-west1"; verified across all 13
// existing CFs + the 3 new M4.5 CFs in commits 2a/2b/2c). The
// httpsCallable client SDK MUST match the deployed region or the
// invocation fails with 'functions/not-found'.
export const functions = getFunctions(app, 'europe-west1');

// Emulator wiring — dev / test only.
// -----------------------------------------------------------------
// Per docs/architecture/dispatches/m-test-1-vitest-emulator-harness.md
// § 3.3 + § 6.4. The flag is OFF (absent) in .env.staging and
// .env.production; this block is dead code in those production
// builds. Vite tree-shakes the `import.meta.env.*` branch when the
// value is `undefined` at build time — verified by the dispatch's
// dead-code grep (`grep -c 'localhost:9099' dist/assets/*.js` → 0
// after `npm run build -- --mode production`).
//
// Local dev: VITE_USE_FIREBASE_EMULATOR=on in .env.development.
// Vitest:    VITE_USE_FIREBASE_EMULATOR=on in .env.test.
//
// Ports come from firebase.json (auth: 9099, firestore: 8765;
// 8765 is chosen to avoid the lead's local SABnzbd on 8080).
//
// Connecting twice (e.g. via HMR re-import) is idempotent for
// connectFirestoreEmulator + connectAuthEmulator in Firebase JS SDK
// v9+; no guard against double-init needed. `disableWarnings: true`
// silences the noisy "Auth Emulator running" console banner that
// would otherwise fire on every test run.
if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'on') {
  connectAuthEmulator(auth, 'http://localhost:9099', {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, 'localhost', 8765);
  // Storage emulator port 9199 is the Firebase default. M4.2 does
  // NOT actually boot the storage emulator (firebase.json doesn't
  // configure it); calling connectStorageEmulator here is forward-
  // compatible wiring for M5+ emulator-driven Storage tests. When
  // no emulator is listening, calls fail at request time — which
  // never happens in M4.2 tests because firebase/storage is mocked.
  connectStorageEmulator(storage, 'localhost', 9199);
  // Functions emulator port 5001 is the Firebase default. M4.5
  // does NOT boot the functions emulator (firebase.json doesn't
  // configure it); calling connectFunctionsEmulator here is
  // forward-compatible wiring for M5+ CF-emulator-driven tests.
  // M4.5's mutation tests mock firebase/functions entirely
  // (per dispatch § 4.4 — pure-unit tests, not emulator-driven),
  // so this branch is dead code in M4.5 but harmless.
  connectFunctionsEmulator(functions, 'localhost', 5001);
}
